import React, {useEffect, useState, useRef} from 'react';
import {useParams, useSearchParams, useNavigate} from 'react-router-dom';
import {
    useRadio,
    useRadioGroup,
    useToast,
    Select,
    Tag,
    TagLabel,
    TagCloseButton,
    Box,
    FlexProps,
    Center,
    Spinner,
    Text,
    Textarea,
    Image,
    VStack,
    HStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    useCheckbox,
    useCheckboxGroup,
    chakra,
    FormControl,
    FormLabel,
    Card,
    CardHeader,
    CardBody,
    Flex,
    GridItem,
    Grid,
    Button,
    type GridItemProps,
    Input,
    IconButton,
    Checkbox,
    CheckboxGroup,
    Icon,
} from '@chakra-ui/react';
import {FaHeart, FaComment, FaEye, FaShare, FaTrash} from 'react-icons/fa';
import {customAuthAxios} from '../../../utils/customAuthAxios';
import {getDjangoToken} from '../../../utils/instagram/services/apiService';
import {TextTextarea} from "../../organisms/externalApi/line/TextTextarea";

interface DmContent {
    id: number;
    title: string;
    content: string;
}

interface InstagramDmContent {
    id: number;
    name: string;
    type: string;
    valid_period?: {
        start_at: string;
        end_at: string;
    };
}

interface InstagramBotSet {
    id: number;
    restaurant_id: string;
    name: string;
    obj: string;
    engagement: string[];
    subject: Record<string, any>;
    start_at: string | null;
    end_at: string | null;
    send_at: number | null;
    instagram_post_id: Record<string, any>;
    bot_description: string;
    bot_image: { url: string }[];
    bot_status: boolean;
    dm_contents: number[];
    target_posts: string[];
}

export const TableHeaderCell = (props: GridItemProps) => {
    return (
        <GridItem
            bg="gray.200"
            textAlign="left"
            py={2}
            fontWeight="bold"
            paddingLeft="10px"
            {...props}
        />
    );
};

// カスタムラジオボタン風ボタン
const RadioCard = (props: any) => {
    const {getInputProps, getRadioProps} = useRadio(props);
    const input = getInputProps();
    const checkbox = getRadioProps();

    return (
        <Box as="label">
            <input {...input} hidden/>
            <Box
                {...checkbox}
                cursor="pointer"
                borderWidth="1px"
                borderRadius="full"
                px={4}
                py={2}
                fontWeight="bold"
                bg={props.isChecked ? 'blue.100' : 'white'}
                color={props.isChecked ? 'blue.600' : 'black'}
                borderColor={props.isChecked ? 'blue.400' : 'gray.300'}
                transition="all 0.2s"
                _hover={{bg: 'blue.50'}}
            >
                {props.children}
            </Box>
        </Box>
    );
};


const CustomCheckbox = (props: any) => {
    const {getInputProps, getCheckboxProps, state} = useCheckbox(props);

    return (
        <Box as="label">
            <input {...getInputProps()} hidden/>
            <Box
                {...getCheckboxProps()}
                px={4}
                py={2}
                borderWidth="1px"
                borderRadius="full"
                cursor="pointer"
                bg={state.isChecked ? "blue.100" : "white"}
                color={state.isChecked ? "blue.600" : "black"}
                fontWeight={state.isChecked ? "bold" : "normal"}
                transition="all 0.2s"
                _hover={{bg: "blue.50"}}
            >
                {props.children}
            </Box>
        </Box>
    );
};

const InstagramBotCreate: React.FC = () => {
    const {restaurantId} = useParams<{ restaurantId: string }>();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('edit');
    const navigate = useNavigate();
    const toast = useToast();
    const [postDetails, setPostDetails] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true); // ローディング状態
    const [dmContents, setDmContents] = useState<InstagramDmContent[]>([]);
    const [selectedDmContents, setSelectedDmContents] = useState<number[]>([]);
    const [selectedDmValue, setSelectedDmValue] = useState('');
    // 選択モード: ラジオボタン
    const postOptions = ['新規投稿作成', '既存の投稿', '全投稿'];
    const [selectedMode, setSelectedMode] = useState('新規投稿作成');
    const [selectedValue, setSelectedValue] = useState('');
    const [selectedEngagements, setSelectedEngagements] = useState<string[]>([]);
    const [selectedEngagementValue, setSelectedEngagementValue] = useState('');
    const {getRootProps, getRadioProps} = useRadioGroup({
        name: 'postType',
        defaultValue: selectedMode,
        onChange: setSelectedMode,
    });
    const {value, getCheckboxProps} = useCheckboxGroup({
        defaultValue: ["お店の投稿"],
    });
    const [operationPeriodMode, setOperationPeriodMode] = useState('設定しない');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const {getRootProps: getOperationPeriodRootProps, getRadioProps: getOperationPeriodRadioProps} = useRadioGroup({
        name: 'operationPeriod',
        defaultValue: operationPeriodMode,
        onChange: setOperationPeriodMode,
    });
    const [sendingTimeMode, setSendingTimeMode] = useState('即時');
    const [hours, setHours] = useState('0');
    const [minutes, setMinutes] = useState('0');
    const {getRootProps: getSendingTimeRootProps, getRadioProps: getSendingTimeRadioProps} = useRadioGroup({
        name: 'sendingTime',
        defaultValue: sendingTimeMode,
        onChange: setSendingTimeMode,
    });
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState<InstagramBotSet>({
        id: 0,
        restaurant_id: restaurantId || '',
        name: '',
        obj: 'post',
        engagement: [],
        subject: {},
        start_at: null,
        end_at: null,
        send_at: null,
        instagram_post_id: {},
        bot_description: '',
        bot_image: [],
        bot_status: true,
        dm_contents: [],
        target_posts: [],
    });
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedTempPosts, setSelectedTempPosts] = useState<string[]>([]);
    const [locationKeyword, setLocationKeyword] = useState('');
    const [locationResults, setLocationResults] = useState<any[]>([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const [hashtagInput, setHashtagInput] = useState('');
    const [hashtags, setHashtags] = useState<string[]>([]);
    const handleAddHashtag = () => {
        const trimmed = hashtagInput.trim().replace(/^#*/, ''); // 先頭の#を除去
        if (trimmed && !hashtags.includes(trimmed)) {
            setHashtags([...hashtags, trimmed]);
        }
        setHashtagInput('');
    };

    const handleRemoveHashtag = (tag: string) => {
        setHashtags(hashtags.filter((t) => t !== tag));
    };

    useEffect(() => {
        const fetchPostDetails = async () => {
            try {
                console.log("FetchPostDetails");

                const djangoToken = await getDjangoToken();
                const response = await customAuthAxios.get(
                    `/external_api/instagram-auth/metrics/${restaurantId}/`,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
                setPostDetails(response.data.metrics);
                console.log(response.data.metrics);

            } catch (error) {
                console.error('Failed to fetch post details:', error);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchDmContents = async () => {
            try {
                const djangoToken = await getDjangoToken();
                const response = await customAuthAxios.get(
                    `/api/restaurants/${restaurantId}/instagram/dm/list_for_bot/`,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
                setDmContents(response.data);
            } catch (error) {
                console.error('Failed to fetch DM contents:', error);
                toast({
                    title: 'エラー',
                    description: 'DMコンテンツの取得に失敗しました',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        const fetchBotSet = async () => {
            if (!editId) return;

            try {
                const djangoToken = await getDjangoToken();
                const response = await customAuthAxios.get(
                    `/api/restaurants/${restaurantId}/instagram/bot/sets/${editId}/`,
                    {
                        headers: { Authorization: `Bearer ${djangoToken}` },
                    }
                );
                const data = response.data;

                // 👇 location.name をフォーム表示用にセット
                if (data.subject?.location?.name) {
                    setLocationKeyword(data.subject.location.name);
                }

                setFormData({
                    id: data.id,
                    restaurant_id: data.restaurant_id || restaurantId || '',
                    name: data.name || '',
                    obj: data.obj || 'post',
                    engagement: data.engagement || [],
                    subject: data.subject || {},
                    start_at: data.start_at || null,
                    end_at: data.end_at || null,
                    send_at: data.send_at || null,
                    instagram_post_id: data.instagram_post_id || {},
                    bot_description: data.bot_description || '',
                    bot_image: data.bot_image || [],
                    bot_status: data.bot_status ?? true,
                    dm_contents: data.dm_contents || [],
                    target_posts: data.target_posts || [],
                });

                if (data.subject?.text) {
                    const textLines = data.subject.text.split('\n');
                    const lastLine = textLines[textLines.length - 1];
                    const foundTags = [...lastLine.matchAll(/#(\w+)/g)].map((m) => m[1]);
                    setHashtags(foundTags.filter((t) => t !== data.subject?.location?.name));
                }
            } catch (error) {
                console.error('Failed to fetch bot set:', error);
                toast({
                    title: 'エラー',
                    description: 'Botセットの取得に失敗しました',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        fetchPostDetails();
        fetchDmContents();
        fetchBotSet();
    }, [restaurantId, editId]);

    // useEffect(() => {
    //     const delayDebounceFn = setTimeout(() => {
    //         searchLocation(locationKeyword);
    //     }, 500); // 入力から500ms後に実行（デバウンス）
    //
    //     return () => clearTimeout(delayDebounceFn);
    // }, [locationKeyword]);

    // const searchLocation = async (keyword: string) => {
    //     if (!keyword || keyword.length < 2) {
    //         setLocationResults([]);
    //         return;
    //     }
    //
    //     setIsSearchingLocation(true);
    //     try {
    //         const djangoToken = await getDjangoToken();
    //         const response = await customAuthAxios.get(
    //             `/api/restaurants/${restaurantId}/instagram/search-location/`,
    //             {
    //                 params: { keyword },
    //                 headers: { Authorization: `Bearer ${djangoToken}` },
    //             }
    //         );
    //         setLocationResults(response.data);
    //     } catch (error) {
    //         console.error('場所の取得に失敗しました', error);
    //         setLocationResults([]);
    //     } finally {
    //         setIsSearchingLocation(false);
    //     }
    // };

    const handleRemove = (val: string) => {
        setFormData(prev => ({
            ...prev,
            target_posts: prev.target_posts.filter((item) => item !== val),
        }));
    };

    const handleAddEngagement = (val: string) => {
        if (val && !selectedEngagements.includes(val)) {
            setSelectedEngagements([...selectedEngagements, val]);
            setSelectedEngagementValue('');
        }
    };

    const handleRemoveEngagement = (val: string) => {
        setSelectedEngagements(selectedEngagements.filter((item) => item !== val));
    };

    const handleAddDmContent = (contentId: number) => {
        if (!selectedDmContents.includes(contentId)) {
            setSelectedDmContents([...selectedDmContents, contentId]);
            setFormData(prev => ({
                ...prev,
                dm_contents: [...prev.dm_contents, contentId],
            }));
            setSelectedDmValue('');
        }
    };

    const handleRemoveDmContent = (contentId: number) => {
        setSelectedDmContents(selectedDmContents.filter(id => id !== contentId));
        setFormData(prev => ({
            ...prev,
            dm_contents: prev.dm_contents.filter(id => id !== contentId),
        }));
    };

    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const newImages = Array.from(files);
            setSelectedImages([...selectedImages, ...newImages]);

            // プレビューURLの作成
            newImages.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreviewUrls(prev => [...prev, reader.result as string]);
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            const newImages = Array.from(files).filter(file => file.type.startsWith('image/'));
            setSelectedImages([...selectedImages, ...newImages]);

            // プレビューURLの作成
            newImages.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreviewUrls(prev => [...prev, reader.result as string]);
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const handleRemoveImage = (index: number) => {
        setSelectedImages(selectedImages.filter((_, i) => i !== index));
        setImagePreviewUrls(imagePreviewUrls.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        const hashtagText = hashtags.map((tag) => `#${tag}`).join(' ');
        const locationTag = formData.subject?.location?.name ? `#${formData.subject.location.name}` : '';
        const combinedText = [hashtagText, locationTag].filter(Boolean).join(' ');

        const payload: InstagramBotSet = {
            ...formData,
            subject: {
                ...formData.subject,
                text: `${formData.subject?.text || ''}\n${combinedText}`.trim(),
            },
            restaurant_id: restaurantId || '',
            start_at: operationPeriodMode === '設定する' ? startDate : null,
            end_at: operationPeriodMode === '設定する' ? endDate : null,
            send_at: sendingTimeMode === '時間を指定する' ? Number(hours) * 60 + Number(minutes) : null,
            engagement: selectedEngagements,
            target_posts: selectedMode === '全投稿' ? ['all'] : formData.target_posts,
            bot_image: imagePreviewUrls.map((url) => ({ url })),
        };

        try {
            const djangoToken = await getDjangoToken();

            if (editId) {
                await customAuthAxios.put(
                    `/api/restaurants/${restaurantId}/instagram/bot/sets/${editId}/`,
                    payload,
                    { headers: { Authorization: `Bearer ${djangoToken}` } }
                );
            } else {
                await customAuthAxios.post(
                    `/api/restaurants/${restaurantId}/instagram/bot/sets/`,
                    payload,
                    { headers: { Authorization: `Bearer ${djangoToken}` } }
                );
                console.log("✅ POST送信データ", payload);
            }

            toast({
                title: '成功',
                description: editId ? 'Botセットを更新しました' : 'Botセットを作成しました',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            navigate(`/restaurants/${restaurantId}/instagram/bot/settings`);
        } catch (error: any) {
            console.error('Botセット作成失敗:', error);
            if (error.response?.data) {
                console.error('サーバーからのエラーメッセージ:', error.response.data);
            }

            toast({
                title: 'エラー',
                description: 'Botセットの作成に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleTargetPostsChange = (value: string[]) => {
        setFormData(prev => ({...prev, target_posts: value}));
    };

    const handleEngagementChange = (value: string[]) => {
        setFormData(prev => ({...prev, engagement: value}));
    };

    const handleDmContentsChange = (value: string[]) => {
        setFormData(prev => ({
            ...prev,
            dm_contents: value.map(v => parseInt(v)),
        }));
    };

    return (
        <Box p={4} width="100%">
            <Card marginY={"20px"}>
                <CardHeader>
                    <Flex justify="space-between">
                        <Text fontWeight="bold" fontSize="lg">
                            {editId ? 'Botセットの編集' : 'Botセットの新規作成'}
                        </Text>
                    </Flex>
                </CardHeader>

                <CardBody>
                    <form onSubmit={handleSubmit}>
                        <VStack spacing={6} align="stretch">
                            <FormControl w={'30%'} paddingBottom={"20px"}>
                                <FormLabel>■BOTセット名</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="名前"
                                    size="sm"
                                    variant="outline"
                                    focusBorderColor="blue.400"
                                />
                            </FormControl>

                            <FormControl w={'100%'} mt={6}>
                                <FormLabel>対象投稿</FormLabel>

                                {/* ラジオ切り替え */}
                                <HStack {...getRootProps()} spacing={4} mb={4}>
                                    {postOptions.map((value) => {
                                        const radio = getRadioProps({value});
                                        return (
                                            <RadioCard key={value} {...radio}>
                                                {value}
                                            </RadioCard>
                                        );
                                    })}
                                </HStack>

                                {/* 選択内容に応じた表示 */}
                                {selectedMode === '新規投稿作成' && (
                                    <>
                                        <FormControl>
                                            <FormLabel>■本文</FormLabel>
                                            <Textarea
                                                w={"50%"}
                                                h={"200px"}
                                                resize="vertical"
                                                placeholder="本文を入力してください"
                                                size="sm"
                                                variant="outline"
                                                focusBorderColor="blue.400"
                                                value={formData.subject?.text || ''}
                                                onChange={(e) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        subject: {
                                                            ...prev.subject,
                                                            text: e.target.value,
                                                        },
                                                    }))
                                                }
                                            />
                                        </FormControl>

                                        <FormControl mt={4}>
                                            <FormLabel>■写真を選択</FormLabel>
                                            <Flex gap={4}>
                                                {imagePreviewUrls.map((url, index) => (
                                                    <Box
                                                        key={index}
                                                        position="relative"
                                                        width="150px"
                                                        height="150px"
                                                    >
                                                        <Image
                                                            src={url}
                                                            alt={`選択された画像 ${index + 1}`}
                                                            objectFit="cover"
                                                            width="100%"
                                                            height="100%"
                                                            borderRadius="md"
                                                        />
                                                        <Button
                                                            position="absolute"
                                                            top={2}
                                                            right={2}
                                                            size="sm"
                                                            colorScheme="red"
                                                            onClick={() => handleRemoveImage(index)}
                                                        >
                                                            削除
                                                        </Button>
                                                    </Box>
                                                ))}
                                                <Box
                                                    width="150px"
                                                    height="150px"
                                                    borderWidth="2px"
                                                    borderStyle="dashed"
                                                    borderColor="gray.300"
                                                    borderRadius="md"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    cursor="pointer"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    _hover={{ borderColor: "blue.400" }}
                                                >
                                                    <VStack spacing={2}>
                                                        <Text fontSize="xl" color="gray.400">+</Text>
                                                        <Text fontSize="sm" color="gray.500">クリックまたはドラッグ＆ドロップで追加</Text>
                                                    </VStack>
                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleImageSelect}
                                                        accept="image/*"
                                                        multiple
                                                        style={{ display: 'none' }}
                                                    />
                                                </Box>
                                            </Flex>
                                        </FormControl>

                                        <Flex w={"75%"} gap={"2"} mt={4}>
                                            <Box w={"49%"}>
                                                <FormControl>
                                                    <FormLabel>■ハッシュタグ</FormLabel>
                                                    <HStack>
                                                        <Input
                                                            placeholder="キーワードを入力（Enterで追加）"
                                                            value={hashtagInput}
                                                            onChange={(e) => setHashtagInput(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    handleAddHashtag();
                                                                }
                                                            }}
                                                        />
                                                    </HStack>
                                                    <HStack mt={2} spacing={2} wrap="wrap">
                                                        {hashtags.map((tag) => (
                                                            <Tag key={tag} size="md" borderRadius="md" variant="subtle" colorScheme="blue">
                                                                <TagLabel>#{tag}</TagLabel>
                                                                <TagCloseButton onClick={() => handleRemoveHashtag(tag)} />
                                                            </Tag>
                                                        ))}
                                                    </HStack>
                                                </FormControl>
                                            </Box>
                                            <Box w={"49%"}>
                                                <FormControl>
                                                    <FormLabel>■場所</FormLabel>
                                                    <Input
                                                        w="100%"
                                                        placeholder="場所名を入力"
                                                        value={locationKeyword}
                                                        onChange={(e) => {
                                                            setLocationKeyword(e.target.value);
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                subject: {
                                                                    ...prev.subject,
                                                                    location: {
                                                                        name: e.target.value
                                                                    }
                                                                }
                                                            }));
                                                        }}
                                                    />
                                                </FormControl>
                                            </Box>
                                        </Flex>
                                    </>
                                )}

                                {selectedMode === '既存の投稿' && (

                                    <Box>
                                        <Button onClick={onOpen} size="sm" colorScheme="blue" mb={2}>
                                            投稿を選択
                                        </Button>

                                        <HStack spacing={2} wrap="wrap">
                                            {formData.target_posts.map((caption) => (
                                                <Tag
                                                    key={caption}
                                                    size="md"
                                                    borderRadius="md"
                                                    variant="subtle"
                                                    colorScheme="gray"
                                                >
                                                    <TagLabel>{caption}</TagLabel>
                                                    <TagCloseButton onClick={() => handleRemove(caption)} />
                                                </Tag>
                                            ))}
                                        </HStack>
                                        <Modal isOpen={isOpen} onClose={onClose} size="xl">
                                            <ModalOverlay />
                                            <ModalContent>
                                                <ModalHeader>既存の投稿を選択</ModalHeader>
                                                <ModalCloseButton />
                                                <ModalBody>
                                                    {Array.isArray(postDetails) ? (
                                                        <Grid templateColumns="repeat(auto-fill, minmax(150px, 1fr))" gap={4}>
                                                            {postDetails.map((post: any) => (
                                                                <Box
                                                                    key={post.id}
                                                                    borderWidth="1px"
                                                                    borderRadius="md"
                                                                    overflow="hidden"
                                                                    cursor="pointer"
                                                                    _hover={{ boxShadow: "md", borderColor: "blue.400" }}
                                                                    onClick={() => {
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            target_posts: [post.id],            // ✅ 投稿本文を保存
                                                                            instagram_post_id: { id: post.id },      // ✅ 投稿IDを保存
                                                                            subject: {
                                                                                ...prev.subject,
                                                                                text: post.caption || "" // キャプションをsubjectに保存
                                                                            }
                                                                        }));
                                                                        onClose(); // モーダルを閉じる
                                                                    }}
                                                                >
                                                                    <Image
                                                                        src={post.media_url}
                                                                        alt={post.caption || "投稿画像"}
                                                                        objectFit="cover"
                                                                        width="100%"
                                                                        height="150px"
                                                                    />
                                                                    <Box p={2}>
                                                                        <Text fontSize="sm" noOfLines={2}>
                                                                            {post.caption || "（キャプションなし）"}
                                                                        </Text>
                                                                        <Text fontSize="xs" color="gray.500">
                                                                            {new Date(post.timestamp).toLocaleDateString()}
                                                                        </Text>
                                                                    </Box>
                                                                </Box>
                                                            ))}
                                                        </Grid>
                                                    ) : (
                                                        <Center><Spinner  /></Center>
                                                    )}
                                                </ModalBody>
                                            </ModalContent>
                                        </Modal>
                                    </Box>
                                )}

                                {selectedMode === '全投稿' && (
                                    <Text fontSize="sm" color="gray.500">
                                        全ての投稿が対象です（選択は不要です）。
                                    </Text>
                                )}
                            </FormControl>

                            <Box>
                                <FormLabel>■エンゲージメントを選択</FormLabel>
                                <Select
                                    placeholder="エンゲージメントを選択"
                                    value={selectedEngagementValue}
                                    onChange={(e) => handleAddEngagement(e.target.value)}
                                    w="300px"
                                    mb={2}
                                >
                                    <option value="いいね">いいね</option>
                                    <option value="コメント">コメント</option>
                                    <option value="保存">保存</option>
                                </Select>

                                <HStack spacing={2} wrap="wrap" mb={4}>
                                    {selectedEngagements.map((engagement) => (
                                        <Tag
                                            key={engagement}
                                            size="md"
                                            borderRadius="md"
                                            variant="subtle"
                                            colorScheme="blue"
                                        >
                                            <TagLabel>{engagement}</TagLabel>
                                            <TagCloseButton onClick={() => handleRemoveEngagement(engagement)} />
                                        </Tag>
                                    ))}
                                </HStack>

                                <FormControl mb={4}>
                                    <FormLabel>■Bot稼働期間</FormLabel>
                                    <HStack {...getOperationPeriodRootProps()} spacing={4} mb={4}>
                                        <RadioCard {...getOperationPeriodRadioProps({value: '設定しない'})}>
                                            稼働期間を設定しない
                                        </RadioCard>
                                        <RadioCard {...getOperationPeriodRadioProps({value: '設定する'})}>
                                            稼働期間を設定する
                                        </RadioCard>
                                    </HStack>

                                    {operationPeriodMode === '設定する' && (
                                        <Flex gap={4} mb={4} w={"50%"} alignItems="center">
                                            <Flex alignItems="center" gap={2}>
                                                <Text whiteSpace="nowrap" minW="60px" mb={0}>開始日</Text>
                                                <Input
                                                    type="date"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                    size="sm"
                                                    variant="outline"
                                                    focusBorderColor="blue.400"
                                                    placeholder="年/月/日"
                                                />
                                            </Flex>
                                            <Text mb={0}>〜</Text>
                                            <Flex alignItems="center" gap={2}>
                                                <Text whiteSpace="nowrap" minW="60px" mb={0}>終了日</Text>
                                                <Input
                                                    type="date"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                    size="sm"
                                                    variant="outline"
                                                    focusBorderColor="blue.400"
                                                    placeholder="年/月/日"
                                                />
                                            </Flex>
                                        </Flex>
                                    )}
                                </FormControl>

                                <FormControl mb={4}>
                                    <FormLabel>■送信タイミング</FormLabel>
                                    <HStack {...getSendingTimeRootProps()} spacing={4} mb={4}>
                                        <RadioCard {...getSendingTimeRadioProps({value: '即時'})}>
                                            即時
                                        </RadioCard>
                                        <RadioCard {...getSendingTimeRadioProps({value: '時間を指定する'})}>
                                            時間を指定する
                                        </RadioCard>
                                    </HStack>

                                    {sendingTimeMode === '時間を指定する' && (
                                        <Flex gap={2} alignItems="center">
                                            <Select
                                                value={hours}
                                                onChange={(e) => setHours(e.target.value)}
                                                w="100px"
                                                size="sm"
                                            >
                                                {[...Array(24)].map((_, i) => (
                                                    <option key={i} value={i}>{i}</option>
                                                ))}
                                            </Select>
                                            <Text mb={0}>時間</Text>
                                            <Select
                                                value={minutes}
                                                onChange={(e) => setMinutes(e.target.value)}
                                                w="100px"
                                                size="sm"
                                            >
                                                {[...Array(60)].map((_, i) => (
                                                    <option key={i} value={i}>{i}</option>
                                                ))}
                                            </Select>
                                            <Text mb={0}>分後</Text>
                                        </Flex>
                                    )}
                                </FormControl>

                                <FormControl mb={4}>
                                    <FormLabel>■DMコンテンツを選択</FormLabel>
                                    <Select
                                        placeholder="DMコンテンツを選択"
                                        value={selectedDmValue}
                                        onChange={(e) => handleAddDmContent(Number(e.target.value))}
                                        w="300px"
                                        mb={2}
                                    >
                                        {dmContents.map((content) => (
                                            <option key={content.id} value={content.id}>
                                                {content.name} ({content.type})
                                            </option>
                                        ))}
                                    </Select>

                                    <HStack spacing={2} wrap="wrap" mb={4}>
                                        {dmContents
                                            .filter(content => selectedDmContents.includes(content.id))
                                            .map((content) => (
                                                <Tag
                                                    key={content.id}
                                                    size="md"
                                                    borderRadius="md"
                                                    variant="subtle"
                                                    colorScheme="gray"
                                                >
                                                    <TagLabel>{content.name} ({content.type})</TagLabel>
                                                    <TagCloseButton onClick={() => handleRemoveDmContent(content.id)}/>
                                                </Tag>
                                            ))}
                                    </HStack>
                                </FormControl>
                            </Box>

                            <Flex justify="flex-end">
                                <Button
                                    type="submit"
                                    colorScheme="blue"
                                    size="sm"
                                    isDisabled={!formData.name}
                                    isLoading={isLoading}
                                    loadingText={editId ? '更新中...' : '作成中...'}
                                >
                                    {editId ? '更新' : '作成'}
                                </Button>
                            </Flex>
                        </VStack>
                    </form>
                </CardBody>
            </Card>
        </Box>
    );
};

export default InstagramBotCreate;
